// services/serviceCostService.js
import apiClient from '../services/api';
import { loadBusinessSettings } from '../utils/businessSettingsHelper';

const serviceCostService = {
  // Get calculated costs for a service
  getServiceCosts: async (serviceId) => {
    try {
      const response = await apiClient.get(`/service-costs/${serviceId}`);
      return response;
    } catch (error) {
      console.error('Error fetching service costs:', error);
      throw error;
    }
  },
  
  // Calculate costs for a single service - FIXED: Use POST instead of PUT
  calculateServiceCosts: async (serviceId, options = {}) => {
    try {
      // Get current business settings
      const businessSettings = await loadBusinessSettings();
      
      // Use provided options or fall back to business settings
      const calculationOptions = {
        combinedRatePerMinute: parseFloat(businessSettings.overhead_per_minute) || 0,
        profitMargin: parseFloat(options.profit_margin || businessSettings.default_profit_margin) || 10,
        vatRate: parseFloat(options.vat_rate || businessSettings.default_vat_rate) || 20,
        is_vat_registered: businessSettings.is_vat_registered || false,
        staffId: options.staffId || null,  // Include staffId in options
        useTargetApproach: options.useTargetApproach !== false // Default to true unless explicitly set to false
      };
      
      console.log('Calculating service costs with options:', calculationOptions);
      
      // Call the API endpoint for service cost calculation - CHANGED to POST and fixed path
      const response = await apiClient.post(`/service-costs/${serviceId}/calculate`, {
        combinedRatePerMinute: calculationOptions.combinedRatePerMinute,
        overhead_per_minute: calculationOptions.combinedRatePerMinute, // Include legacy param for compatibility
        profitMargin: calculationOptions.profitMargin,
        profit_margin: calculationOptions.profitMargin, // Include legacy param for compatibility
        vatRate: calculationOptions.vatRate,
        vat_rate: calculationOptions.vatRate, // Include legacy param for compatibility
        is_vat_registered: calculationOptions.is_vat_registered,
        staffId: calculationOptions.staffId,
        staff_id: calculationOptions.staffId, // Include legacy param for compatibility
        useTargetApproach: calculationOptions.useTargetApproach
      });
      
      return response;
    } catch (error) {
      console.error('Error calculating service costs:', error);
      throw error;
    }
  },
  
  // Calculate service cost locally (for previews or testing)
  calculateServiceCostLocal: async (service, products = [], options = {}) => {
    try {
      // Get current business settings
      const businessSettings = await loadBusinessSettings();
      
      // Extract service time
      const serviceTime = parseFloat(service.time) || 0;
      
      // Use provided options or fall back to business settings
      const calculationOptions = {
        combinedRatePerMinute: parseFloat(businessSettings.overhead_per_minute) || 0,
        profitMargin: parseFloat(options.profitMargin || businessSettings.default_profit_margin) || 10,
        vatRate: parseFloat(options.vatRate || businessSettings.default_vat_rate) || 20,
        is_vat_registered: businessSettings.is_vat_registered || false,
        staffHourlyRate: parseFloat(options.staffHourlyRate) || 0,
        useTargetApproach: options.useTargetApproach !== false // Default to true unless explicitly set to false
      };
      
      // Process product costs
      let productCost = 0;
      const productBreakdown = {};
      
      if (products && products.length > 0) {
        products.forEach(product => {
          const containerSize = parseFloat(product.containerSize || product.qty) || 1;
          const price = parseFloat(product.totalCost || product.price) || 0;
          const usage = parseFloat(product.usage) || 0;
          
          if (containerSize > 0 && price > 0 && usage > 0) {
            const cost = (price / containerSize) * usage;
            productBreakdown[product.name] = parseFloat(cost.toFixed(2));
            productCost += cost;
          }
        });
      }
      
      // Use target-based or margin-based approach based on options
      let result;
      
      if (calculationOptions.useTargetApproach && calculationOptions.combinedRatePerMinute > 0) {
        // Target-based approach
        const overheadComponent = calculationOptions.combinedRatePerMinute * 0.7; // 70% overhead
        const profitComponent = calculationOptions.combinedRatePerMinute * 0.3; // 30% profit
        
        // Calculate staff cost
        const timeHours = serviceTime / 60;
        const staffCost = timeHours * calculationOptions.staffHourlyRate;
        
        // Calculate overhead cost
        const overheadCost = serviceTime * overheadComponent;
        
        // Calculate profit target
        const profitTarget = serviceTime * profitComponent;
        
        // Calculate base cost (product + overhead + staff, without profit)
        const baseCost = productCost + overheadCost + staffCost;
        
        // Calculate recommended price (base cost + profit target)
        const recommendedPrice = baseCost + profitTarget;
        
        // Calculate price with VAT if applicable
        const priceWithVat = calculationOptions.is_vat_registered
          ? recommendedPrice * (1 + (calculationOptions.vatRate / 100))
          : recommendedPrice;
        
        // Calculate effective profit margin
        const effectiveProfitMargin = baseCost > 0 ? (profitTarget / baseCost) * 100 : 0;
        
        result = {
          productCost: parseFloat(productCost.toFixed(2)),
          productBreakdown,
          staffCost: parseFloat(staffCost.toFixed(2)),
          overheadCost: parseFloat(overheadCost.toFixed(2)),
          timeCost: parseFloat((serviceTime * calculationOptions.combinedRatePerMinute).toFixed(2)),
          profitTarget: parseFloat(profitTarget.toFixed(2)),
          baseCost: parseFloat(baseCost.toFixed(2)),
          totalCost: parseFloat(baseCost.toFixed(2)),
          recommendedPrice: parseFloat(recommendedPrice.toFixed(2)),
          priceWithVat: parseFloat(priceWithVat.toFixed(2)),
          effectiveProfitMargin: parseFloat(effectiveProfitMargin.toFixed(2)),
          profitMargin: calculationOptions.profitMargin,
          vatRate: calculationOptions.vatRate,
          calculationMethod: 'target'
        };
      } else {
        // Margin-based approach (legacy)
        // Calculate staff cost
        const timeHours = serviceTime / 60;
        const staffCost = timeHours * calculationOptions.staffHourlyRate;
        
        // Calculate overhead cost using overhead per minute
        const overheadCost = serviceTime * calculationOptions.combinedRatePerMinute;
        
        // Calculate base cost
        const baseCost = productCost + staffCost + overheadCost;
        
        // Calculate profit amount
        const profitAmount = baseCost * (calculationOptions.profitMargin / 100);
        
        // Calculate recommended price
        const recommendedPrice = baseCost + profitAmount;
        
        // Calculate price with VAT if applicable
        const priceWithVat = calculationOptions.is_vat_registered
          ? recommendedPrice * (1 + (calculationOptions.vatRate / 100))
          : recommendedPrice;
        
        result = {
          productCost: parseFloat(productCost.toFixed(2)),
          productBreakdown,
          staffCost: parseFloat(staffCost.toFixed(2)),
          overheadCost: parseFloat(overheadCost.toFixed(2)),
          timeCost: parseFloat(staffCost.toFixed(2)),
          baseCost: parseFloat(baseCost.toFixed(2)),
          totalCost: parseFloat(baseCost.toFixed(2)),
          grossProfit: parseFloat(profitAmount.toFixed(2)),
          recommendedPrice: parseFloat(recommendedPrice.toFixed(2)),
          priceWithVat: parseFloat(priceWithVat.toFixed(2)),
          profitMargin: calculationOptions.profitMargin,
          vatRate: calculationOptions.vatRate,
          calculationMethod: 'margin'
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error calculating service cost locally:', error);
      throw error;
    }
  },
  
  // Update the actual stored costs for a service - FIXED: changed endpoint
  updateServiceCosts: async (serviceId, costs) => {
    try {
      // Make sure staff_id is preserved in the request
      const response = await apiClient.put(`/service-costs/${serviceId}`, {
        ...costs,
        // Make sure we explicitly include staff_id
        staff_id: costs.staff_id || costs.staffId
      });
      return response;
    } catch (error) {
      console.error('Error updating service costs:', error);
      throw error;
    }
  },
  
  // Calculate costs for all services
  calculateAllServiceCosts: async (options = {}) => {
    try {
      // Get current business settings
      const businessSettings = await loadBusinessSettings();
      
      // Use provided options or fall back to business settings
      const calculationOptions = {
        combinedRatePerMinute: parseFloat(options.combinedRatePerMinute || businessSettings.overhead_per_minute) || 0,
        profitMargin: parseFloat(options.profitMargin || businessSettings.default_profit_margin) || 10,
        vatRate: parseFloat(options.vatRate || businessSettings.default_vat_rate) || 20,
        is_vat_registered: options.is_vat_registered !== undefined ? options.is_vat_registered : businessSettings.is_vat_registered || false,
        useTargetApproach: options.useTargetApproach !== false // Default to true unless explicitly set to false
      };
      
      console.log('Calculating all service costs with options:', calculationOptions);
      
      // Call the API endpoint
      const response = await apiClient.post('/service-costs/calculate-all', {
        combinedRatePerMinute: calculationOptions.combinedRatePerMinute,
        overhead_per_minute: calculationOptions.combinedRatePerMinute, // Include legacy param for compatibility
        profitMargin: calculationOptions.profitMargin,
        profit_margin: calculationOptions.profitMargin, // Include legacy param for compatibility
        vatRate: calculationOptions.vatRate,
        vat_rate: calculationOptions.vatRate, // Include legacy param for compatibility
        is_vat_registered: calculationOptions.is_vat_registered,
        useTargetApproach: calculationOptions.useTargetApproach
      });
      
      return response;
    } catch (error) {
      console.error('Error calculating all service costs:', error);
      throw error;
    }
  },
  
  // Apply recommended price to service's current price
  applyRecommendedPrice: async (serviceId) => {
    try {
      const response = await apiClient.post(`/service-costs/${serviceId}/apply`);
      return response;
    } catch (error) {
      console.error('Error applying recommended price:', error);
      throw error;
    }
  }
};

export default serviceCostService;